"use client";

import type { CSSProperties, ReactNode, SyntheticEvent } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDraggableResizable } from "@/hooks/useDraggableResizable";
import { useRestoreFocusOnClose } from "@/hooks/useRestoreFocusOnClose";
import Loader from "@/components/Loader";
import WarningModal from "@/components/WarningModal";

/** Matches `AdvancedModal` Cancel button styling. */
export const questionnaireAdvancedModalCancelButtonSx = {
  backgroundColor: "#E8E5FF",
  color: "#6f737a",
  border: "1px solid #E8E5FF",
  borderRadius: "6px",
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  textTransform: "uppercase",
  boxShadow: "none",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#cfcce8",
    borderColor: "#E8E5FF",
    color: "#6f737a",
    transform: "translateY(-1px)",
    boxShadow: "none",
  },
} as const;

/** Default primary (Submit) styling — matches `AdvancedModal` contained submit. */
export const questionnaireAdvancedModalPrimaryButtonSx = {
  backgroundColor: "#63A821",
  color: "#fff",
  borderRadius: "6px",
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  textTransform: "uppercase",
  "&:hover": { backgroundColor: "#5a951d" },
} as const;

/** @deprecated Prefer {@link questionnaireAdvancedModalCancelButtonSx} (OSHA shim during migration). */
export const oshaQuestionnaireAdvancedModalCancelButtonSx = questionnaireAdvancedModalCancelButtonSx;

/** @deprecated Prefer {@link questionnaireAdvancedModalPrimaryButtonSx}. */
export const oshaQuestionnaireAdvancedModalPrimaryButtonSx = questionnaireAdvancedModalPrimaryButtonSx;

export type QuestionnaireAdvancedModalShellHandle = {
  /** Close after unsaved-changes check (same as title X / backdrop / escape when enabled). */
  requestClose: () => void;
};

export type QuestionnaireAdvancedModalShellProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Passed to the title `Typography` `style` prop (same as AdvancedModal `titleStyle`). */
  titleStyle?: CSSProperties;
  subtitle?: ReactNode;
  children: ReactNode;
  /** When omitted, footer is hidden (same idea as `hideDialogActions`). */
  actions?: ReactNode;
  /** Full-width overlay over body + actions (not the title bar). */
  loading?: boolean;
  loadingMessage?: string;
  /**
   * When the paper has no explicit width from resize, Dialog `maxWidth`.
   * AdvancedModal uses `"md"` when not sized.
   */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * When true, backdrop / escape / title close and {@link QuestionnaireAdvancedModalShellHandle.requestClose}
   * show {@link WarningModal} before calling `onClose`.
   */
  hasUnsavedChanges?: boolean;
  /** Discard prompt when `hasUnsavedChanges` is true (defaults below). */
  discardConfirmTitle?: string;
  discardConfirmMessage?: string;
  /**
   * When the paper has no explicit height from resize, Dialog `maxHeight`.
   */
  maxHeight?: string;
  /** Minimum height of the scrollable body (`DialogContent`) only — does not resize the paper. */
  contentMinHeight?: string;
  width?: string;
  height?: string;
  disableBackdropClick?: boolean;
};

/** @deprecated Prefer {@link QuestionnaireAdvancedModalShellProps}. */
export type OshaQuestionnaireAdvancedModalShellProps = QuestionnaireAdvancedModalShellProps;

/**
 * Visual shell copied from `AdvancedModal` (draggable navy title bar, padding, resize handle, action bar).
 * Shared questionnaire **action** and **transition** modals (see `questionnaire-transitions/`).
 */
export const QuestionnaireAdvancedModalShell = forwardRef<
  QuestionnaireAdvancedModalShellHandle,
  QuestionnaireAdvancedModalShellProps
>(function QuestionnaireAdvancedModalShell(
  {
    open,
    onClose,
    title,
    titleStyle,
    subtitle,
    children,
    actions,
    loading = false,
    loadingMessage = "Submitting…",
    maxWidth: maxWidthProp = "md",
    maxHeight: maxHeightProp = "calc(100vh - 70px)",
    contentMinHeight,
    hasUnsavedChanges = false,
    discardConfirmTitle = "Unsaved changes",
    discardConfirmMessage = "You have unsaved changes. Are you sure you want to close?",
    width,
    height,
    disableBackdropClick = true,
  },
  ref,
) {
  const [unsavedWarningOpen, setUnsavedWarningOpen] = useState(false);

  useRestoreFocusOnClose(open);

  useEffect(() => {
    if (!open) setUnsavedWarningOpen(false);
  }, [open]);

  const performClose = useCallback(() => {
    setUnsavedWarningOpen(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setUnsavedWarningOpen(true);
      return;
    }
    onClose();
  }, [hasUnsavedChanges, onClose]);

  useImperativeHandle(ref, () => ({ requestClose }), [requestClose]);

  const {
    paperProps: draggablePaperProps,
    handleMouseDown,
    handleResizeStart,
  } = useDraggableResizable(open);

  const hasWidth = draggablePaperProps.sx.width || width;
  const maxWidth = hasWidth ? false : maxWidthProp;
  const fullWidth = !hasWidth;

  const handleDialogClose = (_event: SyntheticEvent, reason: "backdropClick" | "escapeKeyDown") => {
    if (disableBackdropClick && reason === "backdropClick") return;
    requestClose();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        ...draggablePaperProps,
        sx: {
          ...draggablePaperProps.sx,
          width: draggablePaperProps.sx.width ?? width,
          height: draggablePaperProps.sx.height ?? height,
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(21, 60, 127, 0.06)",
          fontFamily: "Poppins, sans-serif",
          display: "flex",
          flexDirection: "column",
          maxHeight: maxHeightProp,
        },
      }}
    >
      <DialogTitle
        onMouseDown={handleMouseDown}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "15px 15px 20px 15px",
          fontFamily: "Poppins, sans-serif",
          bgcolor: "#153C7F",
          color: "#fff",
          cursor: "grab",
          "&:active": {
            cursor: "grabbing",
          },
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip
            title="Drag to move"
            componentsProps={{
              popper: {
                sx: {
                  zIndex: 1800,
                },
              },
            }}
          >
            <DragIndicatorIcon
              sx={{
                fontSize: 18,
                color: "rgba(255, 255, 255, 0.8)",
                cursor: "grab",
                "&:hover": {
                  color: "#fff",
                },
              }}
            />
          </Tooltip>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            component="div"
            variant="h6"
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#fff",
              fontFamily: "Poppins, sans-serif",
              textTransform: "uppercase",
            }}
            style={titleStyle}
          >
            {title}
          </Typography>
          {subtitle != null ? (
            <Typography
              component="div"
              variant="h6"
              sx={{ fontSize: "16px", fontWeight: 500, color: "#fff", fontFamily: "Poppins, sans-serif" }}
            >
              {subtitle}
            </Typography>
          ) : null}
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={requestClose}
          sx={{
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <DialogContent
          sx={{
            flex: "1 1 auto",
            minHeight: contentMinHeight ?? 0,
            overflow: "auto",
            padding: "24px",
            ...(contentMinHeight != null
              ? { display: "flex", flexDirection: "column" as const }
              : null),
          }}
        >
          {children}
        </DialogContent>
        {actions != null ? (
          <DialogActions
            sx={{
              flexShrink: 0,
              p: 3,
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {actions}
            </Box>
          </DialogActions>
        ) : null}
        {loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 255, 255, 0.88)",
              backdropFilter: "blur(2px)",
              pointerEvents: "auto",
            }}
          >
            <Loader message={loadingMessage} visible fullPage/>
          </Box>
        ) : null}
      </Box>

      <Box
        onMouseDown={handleResizeStart}
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "20px",
          height: "20px",
          cursor: "nwse-resize",
          backgroundColor: "#153C7F",
          borderTopLeftRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1,
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "4px",
            right: "4px",
            width: 0,
            height: 0,
            borderStyle: "solid",
            borderWidth: "0 0 8px 8px",
            borderColor: "transparent transparent #fff transparent",
          },
        }}
      >
        <Tooltip title="Drag to resize" componentsProps={{ popper: { sx: { zIndex: 1800 } } }}>
          <Box sx={{ width: "100%", height: "100%" }} />
        </Tooltip>
      </Box>
    </Dialog>
    <WarningModal
      open={unsavedWarningOpen}
      onClose={() => setUnsavedWarningOpen(false)}
      type="warning"
      title={discardConfirmTitle}
      message={discardConfirmMessage}
      primaryButtonText="DISCARD"
      onPrimary={performClose}
      secondaryButtonText="CANCEL"
      onSecondary={() => setUnsavedWarningOpen(false)}
      zIndex={1900}
    />
    </>
  );
});

QuestionnaireAdvancedModalShell.displayName = "QuestionnaireAdvancedModalShell";

/** @deprecated Prefer {@link QuestionnaireAdvancedModalShell}. */
export const OshaQuestionnaireAdvancedModalShell = QuestionnaireAdvancedModalShell;
